// ComputeRuntimeDetail.jsx — Left TOC + scroll-spy + RIGHT Sponsored
// (Bootstrap + React Router v4/v5)
// Style: matches your newer “template + example” pages (InterviewGamePlanDetail.jsx, etc.)
// Includes: TemplateCard + ExampleCard with right-side badges, “Say” callouts, Pitfalls lists, and Code cards.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ================== AdSense helpers ================== */
const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {}
    }
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <h3 className="h6 text-uppercase text-muted mb-2">Sponsored</h3>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* ================== small UI helpers ================== */
const Section = ({ id, title, children }) => (
  <section id={id} className="mb-5">
    <h2 className="h4 mb-2">{title}</h2>
    {children}
    <div className="mt-3">
      <a href="#top" className="small text-muted">
        ↑ Back to top
      </a>
    </div>
  </section>
);

function BadgeRight({ children, tone = "secondary" }) {
  return (
    <span className={`badge bg-${tone} text-white`} style={{ whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function LabeledCard({ label, tone, title, children }) {
  return (
    <div className="card border-0 shadow-sm mt-3">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
          <div className="fw-semibold">{title}</div>
          <BadgeRight tone={tone}>{label}</BadgeRight>
        </div>
        <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

const TemplateCard = ({ title = "Template", children }) => (
  <LabeledCard label="Template" tone="secondary" title={title}>
    {children}
  </LabeledCard>
);

const ExampleCard = ({ title = "Example", children }) => (
  <LabeledCard label="Example" tone="success" title={title}>
    {children}
  </LabeledCard>
);

function CodeCard({ title = "Template — code / snippet", children }) {
  return (
    <div className="card border-0 shadow-sm mt-3">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
          <div className="fw-semibold">{title}</div>
          <BadgeRight tone="secondary">Template</BadgeRight>
        </div>
        <pre
          className="bg-light rounded p-3 border small mb-0"
          style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
        >
          <code>{children}</code>
        </pre>
      </div>
    </div>
  );
}

function SayCallout({ children }) {
  return (
    <div className="alert alert-primary d-flex align-items-center mb-3" role="alert">
      <div className="me-2 fw-semibold">Say</div>
      <div className="mb-0">{children}</div>
    </div>
  );
}

function Pitfalls({ items }) {
  return (
    <div className="alert alert-warning small mt-3 mb-0">
      <div className="fw-semibold mb-1">Pitfalls</div>
      <ul className="mb-0">
        {items.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

/* ================== TOC ================== */
const TOC = [
  { id: "threads-async", label: "A) Threads vs async I/O; pools; N+1" },
  { id: "serverless", label: "B) Containers vs serverless" },
  { id: "k8s", label: "C) Scheduling & bin packing (K8s)" },
  { id: "discovery-config", label: "D) Discovery, config, feature flags" },
  { id: "handy", label: "Handy rules & snippets" },
];

/* ================== page ================== */
export default function ComputeRuntimeDetail() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [activeId, setActiveId] = useState(TOC[0].id);

  // scroll-spy
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActiveId(e.target.id);
      });
    }, opts);

    TOC.forEach(({ id: sectionId }) => {
      const el = document.getElementById(sectionId);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, []);

  // Example scenario used consistently across the page
  const app = useMemo(
    () => ({
      name: "Facebook-like Home Feed",
      paths: {
        feedRead: "GET /v1/feed",
        postCreate: "POST /v1/posts",
        follow: "POST /v1/users/{id}/follow",
      },
      components: ["API Gateway", "Feed Service", "Graph Service", "Media Service", "Redis", "Post DB"],
      painPoints: ["p99 latency spikes", "DB connection exhaustion", "deploy-time thundering herds"],
    }),
    []
  );

  return (
    <div className="container-lg py-4" id="top">
      {/* breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>Compute &amp; runtime</span>
      </nav>

      {/* header */}
      <header
        className="mb-4 text-center rounded-3 p-3 p-md-4"
        style={{
          background: "linear-gradient(135deg, rgba(13,110,253,.06), rgba(32,201,151,.07))",
        }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-1">12) Compute &amp; runtime — detailed guide</h1>
          <p className="text-muted mb-0">
            Concurrency models, pools, serverless vs containers, Kubernetes sizing, and safe runtime controls —
            explained with a {app.name} example.
          </p>
        </div>
      </header>

      <div className="row g-4">
        {/* LEFT TOC */}
        <aside className="col-12 col-xl-2 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h3 className="h6 text-uppercase text-muted mb-2">On this page</h3>
                <nav className="nav flex-column small">
                  {TOC.map(({ id: sectionId, label }) => (
                    <a
                      key={sectionId}
                      href={`#${sectionId}`}
                      className={`nav-link px-0 ${activeId === sectionId ? "fw-semibold text-primary" : ""}`}
                      aria-current={activeId === sectionId ? "true" : undefined}
                    >
                      {label}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            <div className="d-none d-xl-block small text-muted mt-2">
              Tip: In interviews, tie runtime choices to latency, cost, and failure modes.
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7">
          {/* A) Threads vs async */}
          <Section id="threads-async" title="A) Threads vs async I/O; pools; N+1 pitfalls">
            <SayCallout>
              “I/O-bound work benefits from async multiplexing; CPU-bound work needs bounded worker pools. Everything
              is bounded: threads, queues, and connection pools.”
            </SayCallout>

            <h3 className="h6 text-uppercase text-muted">Threads vs async (how to choose)</h3>
            <ul className="mb-0">
              <li>
                <strong>Threads (blocking model):</strong> simplest mental model; good when blocking calls dominate and
                concurrency is moderate. Downsides: context switching + memory per thread.
              </li>
              <li>
                <strong>Async/event loop:</strong> handles massive I/O concurrency (many sockets) if handlers remain
                non-blocking. Downsides: one accidental blocking call can stall everything (p99 spikes).
              </li>
              <li>
                <strong>Hybrid is common:</strong> event loop for I/O + worker pool for CPU-heavy work (compression,
                crypto, image resizing).
              </li>
              <li>
                <strong>Modern runtimes:</strong> Go goroutines, Java virtual threads, and async frameworks reduce pain,
                but you still must bound concurrency and pools.
              </li>
            </ul>

            <CodeCard title="Thread pool sizing — template (rule of thumb)">
{`threads ≈ cores × (1 + wait_time / compute_time)

Examples:
• Mostly CPU: wait/compute ~ 0 → threads ≈ cores
• 80% waiting, 20% compute: wait/compute = 4 → threads ≈ cores × 5

Always:
• Keep queues bounded
• Fail fast (429/503) once buffers are full
• Separate pools per dependency (DB vs external HTTP) to avoid head-of-line blocking`}
            </CodeCard>

            <h3 className="h6 text-uppercase text-muted mt-3">Connection pools (DB / HTTP / gRPC)</h3>
            <ul className="mb-0">
              <li>
                <strong>Goal:</strong> protect backends by bounding in-flight calls (your system survives spikes).
              </li>
              <li>
                <strong>DB pool math:</strong> pool_per_pod × replicas ≤ DB max connections (leave headroom for admin,
                migrations, replicas).
              </li>
              <li>
                <strong>HTTP:</strong> keep-alives on; prefer HTTP/2 multiplexing; cap per-origin concurrency.
              </li>
              <li>
                <strong>Timeout layering:</strong> connect timeout, read timeout, overall deadline — all must fit within
                endpoint SLO.
              </li>
            </ul>

            <TemplateCard title="Timeout & retry budget — template">
{`If your API SLO is 800ms:
• Gateway budget: 50ms
• Service compute: 200ms
• DB budget: 250ms
• Downstream budget: 250ms
• Total: 750ms (leave margin)

Retries:
• Only for idempotent ops (or with idempotency key)
• Total retry budget ≤ remaining time (don’t exceed SLO)
• Use exponential backoff + full jitter`}
            </TemplateCard>

            <h3 className="h6 text-uppercase text-muted mt-3">N+1 pitfalls (DB and microservices)</h3>
            <ul className="mb-0">
              <li>
                <strong>DB N+1:</strong> 1 query for the list + 1 query per row (feeds are a classic).
              </li>
              <li>
                <strong>Service N+1:</strong> 1 feed call + N profile calls, or GraphQL resolvers doing per-item lookups.
              </li>
              <li>
                <strong>Fixes:</strong> batch APIs, joins/IN queries, materialized read models (CQRS), GraphQL DataLoader,
                caching with bounded TTL.
              </li>
            </ul>

            <ExampleCard title={`${app.name} — runtime choices`}>
{`For ${app.paths.feedRead}:
• Feed read is I/O-heavy: async/non-blocking clients (HTTP/2) + bounded concurrency.
• CPU tasks (ranking / feature computation) run in a bounded worker pool.
• DB pool per pod is small; total across pods fits DB max connections.
• N+1 avoided by:
  - batched user/profile fetch
  - precomputed “fanout read model” or “timeline table”
  - GraphQL DataLoader if using GraphQL`}
            </ExampleCard>

            <Pitfalls
              items={[
                "Blocking inside an event loop (p99 spikes and timeouts)",
                "Unbounded queues that “hide” overload until you OOM",
                "Fleet-wide DB pool explosion after scaling up or a deploy",
                "Long timeouts that trigger retry storms during partial outages",
                "N+1 hidden behind caches until the cache misses during a cold start",
              ]}
            />
          </Section>

          {/* in-article ad */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90 }} />
            </div>
          )}

          {/* B) Containers vs serverless */}
          <Section id="serverless" title="B) Containers vs serverless (cold starts, concurrency, cost)">
            <SayCallout>
              “I use containers for steady, latency-sensitive services and streaming workers. I use serverless for
              spiky, event-driven tasks — with a plan for cold starts and limits.”
            </SayCallout>

            <div className="row g-3">
              <div className="col-12 col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="fw-semibold mb-2">Containers</div>
                    <ul className="mb-0 text-muted small">
                      <li>Warm processes; predictable p99</li>
                      <li>Long-lived connections (streams, WebSockets)</li>
                      <li>Great for consumers, schedulers, stateful-ish services</li>
                      <li>You manage patching, scaling policy, bin packing</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="fw-semibold mb-2">Serverless</div>
                    <ul className="mb-0 text-muted small">
                      <li>Scale-to-zero; burst handling; per-request billing</li>
                      <li>Great for webhooks, cron-like tasks, glue code</li>
                      <li>Cold starts; max runtime; local disk is ephemeral</li>
                      <li>Concurrency caps and vendor limits matter</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <TemplateCard title="Serverless cold-start mitigation — template">
{`Mitigate cold starts:
• Provisioned/warm concurrency for critical paths
• Keep package/image small (trim deps, avoid huge layers)
• Lazy-init heavy objects (ML models, large SDKs)
• Reuse clients (HTTP pools, DB proxies) across invocations
• Raise per-instance concurrency when safe (watch memory/CPU)

Guardrails:
• Set strict timeouts and concurrency limits
• Use queues (SQS/Kafka) to smooth spikes into downstreams`}
            </TemplateCard>

            <ExampleCard title={`${app.name} — where serverless fits`}>
{`• ${app.paths.postCreate}: image processing or fanout jobs can be serverless (async).
• ${app.paths.feedRead}: keep in containers (latency + warm caches).
• Use queues between the API and async workers to prevent burst overload.`}
            </ExampleCard>

            <Pitfalls
              items={[
                "Assuming serverless is ‘infinite scale’ (account + regional caps exist)",
                "Cold starts during sudden traffic spikes on user-facing endpoints",
                "Using serverless for long-lived connections (WebSockets) without plan",
                "Relying on ephemeral disk for durability",
              ]}
            />
          </Section>

          {/* C) K8s */}
          <Section id="k8s" title="C) Kubernetes scheduling & bin packing (requests/limits, HPA, HA)">
            <SayCallout>
              “Requests are for scheduling guarantees, limits are for protection. I avoid CPU limits on latency-critical
              pods and keep memory headroom to prevent OOMKills.”
            </SayCallout>

            <h3 className="h6 text-uppercase text-muted">Requests vs limits (why it matters)</h3>
            <ul className="mb-0">
              <li>
                <strong>CPU:</strong> limits can cause throttling (CFS) → latency spikes. Prefer realistic requests; use
                limits carefully.
              </li>
              <li>
                <strong>Memory:</strong> exceeding limit ⇒ OOMKill. Keep headroom; watch heap + off-heap + native.
              </li>
              <li>
                <strong>QoS:</strong> Guaranteed (req=limit) vs Burstable (req&lt;limit). Burstable is often best with
                good requests.
              </li>
            </ul>

            <TemplateCard title="K8s sizing — template">
{`How I set requests:
• CPU request ≈ p95 CPU usage (per pod)
• Memory request ≈ p95 memory usage (per pod) + safety buffer
• Avoid CPU limits on p99-sensitive services; if using limits, ensure you test throttling impact

High availability:
• Spread across zones (topologySpreadConstraints)
• Pod disruption budgets for safe upgrades
• Anti-affinity for critical services when needed`}
            </TemplateCard>

            <h3 className="h6 text-uppercase text-muted mt-3">Autoscaling signals</h3>
            <ul className="mb-0">
              <li>
                <strong>APIs:</strong> scale on RPS/pod, in-flight, p95 latency, queue depth (custom metrics) — not CPU only.
              </li>
              <li>
                <strong>Consumers:</strong> scale on lag + processing time; cap per-partition concurrency.
              </li>
              <li>
                <strong>Coordination:</strong> scale safely with DB pools and downstream quotas.
              </li>
            </ul>

            <ExampleCard title={`${app.name} — K8s approach`}>
{`• Feed Service: HPA uses RPS/pod + p95 latency; stabilization prevents flapping.
• Zone spread: keep replicas across AZs to survive an AZ failure.
• Memory headroom avoids OOMKills during traffic spikes or cache warmups.
• DB pool size is adjusted as replicas change so fleet_total ≤ DB max connections.`}
            </ExampleCard>

            <Pitfalls
              items={[
                "Tight CPU limits throttling latency-critical pods",
                "All replicas landing in one AZ (no topology spread)",
                "HPA on CPU for I/O-bound services (wrong signal)",
                "Scaling the API without scaling downstream capacity/pools",
              ]}
            />
          </Section>

          {/* D) Discovery/config/flags */}
          <Section id="discovery-config" title="D) Service discovery, configuration & feature flags">
            <SayCallout>
              “Service discovery should be boring (stable names). Config changes should be audited and safe. Risky paths
              must be behind kill switches.”
            </SayCallout>

            <h3 className="h6 text-uppercase text-muted">Service discovery</h3>
            <ul className="mb-0">
              <li>
                <strong>Default:</strong> DNS-based discovery (Kubernetes Services) with stable virtual names.
              </li>
              <li>
                <strong>Service mesh (optional):</strong> mTLS, retries, outlier ejection, traffic splitting, observability.
              </li>
              <li>
                <strong>Client hygiene:</strong> deadlines/timeouts, jittered retries, and circuit breakers.
              </li>
            </ul>

            <h3 className="h6 text-uppercase text-muted mt-3">Configuration</h3>
            <ul className="mb-0">
              <li>Hierarchical config: defaults → environment → region/tenant → runtime flags.</li>
              <li>Schema validation for config changes; hot reload only for safe settings.</li>
              <li>Audit who changed what; canary config rollout before full blast.</li>
            </ul>

            <h3 className="h6 text-uppercase text-muted mt-3">Feature flags</h3>
            <ul className="mb-0">
              <li>Kill switches for risky features; percentage rollouts; per-tenant allowlists.</li>
              <li>“Flag debt” policy: owner + expiry date; remove flags after rollout completes.</li>
            </ul>

            <ExampleCard title={`${app.name} — safe operations`}>
{`• Kill switch: disable “re-ranking v2” if p99 worsens, while keeping ${app.paths.feedRead} running.
• Canary: route 1% of traffic to a new image pipeline, auto-rollback on error/latency deltas.
• Config: DB pool sizing changes go through staged rollout + monitoring.`}
            </ExampleCard>

            <Pitfalls
              items={[
                "Config drift and no audit trail",
                "Leaking secrets via ConfigMaps or logs",
                "Permanent feature flags increasing complexity over time",
                "Blind retries in the mesh without budgets (cascading failures)",
              ]}
            />
          </Section>

          {/* Handy */}
          <Section id="handy" title="Handy rules & snippets (quote these in interviews)">
            <CodeCard title="Rules of thumb — compute & runtime">
{`Concurrency:
• I/O-bound → async multiplexing + bounded in-flight
• CPU-bound → bounded worker pool sized to cores and wait/compute ratio
• Never allow unbounded queues

Pools:
• DB pool sanity: pool_per_pod × replicas ≤ DB_max_conn
• HTTP: reuse connections, prefer HTTP/2 multiplexing, cap per-origin concurrency

Timeouts:
• Timeouts fit inside the endpoint SLO (layered budgets)
• Retries only within remaining budget, with full jitter

K8s:
• Requests ≈ p95 usage; keep memory headroom
• Avoid CPU limits on p99-sensitive services (throttling risk)
• HPA on meaningful signals: RPS/pod, p95, queue depth; stabilization windows

Flags:
• Every risky feature behind a kill switch
• Remove flags after rollout (flag debt policy)`}
            </CodeCard>
          </Section>

          {/* Optional bottom ad */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90 }} />
            </div>
          )}
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
